import React, { useContext } from 'react';
import { LocaleContext } from '../../../components/LocaleContext';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { postRequest } from '../../../api/ApiController';
import UtilContext from '../../../components/UtilContext';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { SEMAFOR_COLOR_OPTIONS, getSemaforColorClass } from '../../../utils/moduleSettings';
import { uiT } from '../../../utils/uiTranslate';

const getOptions = () => [
  { label: uiT('Common.Off'), value: '0' },
  { label: uiT('Common.On'), value: '1' },
];

const COLOR_VALUES = ['red', 'orange', 'yellow', 'green', 'gray', 'blue', 'black'];

const getSemaforKeys = () => [
  { key: 'mntn_semafor_0', label: uiT('Common.ExpBucket.After') },
  { key: 'mntn_semafor_15', label: uiT('Common.ExpBucket.15') },
  { key: 'mntn_semafor_30', label: uiT('Common.ExpBucket.30') },
  { key: 'mntn_semafor_45', label: uiT('Common.ExpBucket.45') },
  { key: 'mntn_semafor_60', label: uiT('Common.ExpBucket.60') },
  { key: 'mntn_semafor_60_plus', label: uiT('Common.ExpBucket.61plus') },
  { key: 'mntn_semafor_dea', label: uiT('MaintenanceGeneralSettings.Text.DEA_deaktivovany_certifikat') },
];

// Farby pre grafy v Náhľade údržby (4 segmenty: Po expirácii, 15 dní, 30 dní, OK)
const getGraphMaintenanceKeys = () => [
  { key: 'mntn_graph_0', label: uiT('Common.AfterExpiration') },
  { key: 'mntn_graph_15', label: uiT('Common.DaysToExp15') },
  { key: 'mntn_graph_30', label: uiT('Common.DaysToExp30') },
  { key: 'mntn_graph_ok', label: uiT('Common.EligibleOk') },
];

function getSetting(module_settings, key, defaultValue = '1') {
  const row = (module_settings || []).find(s => s.module === 'maintenance' && s.key === key);
  const v = row ? row.value : defaultValue;
  if (v == null || v === '') return defaultValue;
  return (v === 0 || v === '0') ? '0' : (v === 1 || v === '1') ? '1' : String(v);
}

function getSemaforSetting(module_settings, key, defaultValue = 'orange') {
  const row = (module_settings || []).find(s => s.module === 'maintenance' && s.key === key);
  const v = row ? row.value : defaultValue;
  if (v == null || v === '') return defaultValue;
  return COLOR_VALUES.includes(v) ? v : defaultValue;
}

function getColorSetting(module_settings, key, defaultValue = 'orange') {
  const row = (module_settings || []).find(s => s.module === 'maintenance' && s.key === key);
  const v = row ? row.value : defaultValue;
  return v != null && v !== '' ? String(v) : defaultValue;
}

export default function MaintenanceGeneralSettings() {
    const [state, dispatch] = useContext(DatabaseContext);
    const { toast } = useContext(UtilContext);
  const { t } = useContext(LocaleContext);

    const module_settings = state.module_settings || [];
    const inNotifications = getSetting(module_settings, 'show_deactivated_in_notifications');
    const inLists = getSetting(module_settings, 'show_deactivated_in_lists');
    const excludedMachines = getSetting(module_settings, 'show_excluded_machines', '0');
    const excludedCompanies = getSetting(module_settings, 'show_excluded_service_companies', '0');

    const settingInNotif = module_settings.find(s => s.module === 'maintenance' && s.key === 'show_deactivated_in_notifications');
    const settingInLists = module_settings.find(s => s.module === 'maintenance' && s.key === 'show_deactivated_in_lists');
    const settingExcludedMachines = module_settings.find(s => s.module === 'maintenance' && s.key === 'show_excluded_machines');
    const settingExcludedCompanies = module_settings.find(s => s.module === 'maintenance' && s.key === 'show_excluded_service_companies');

    async function changeInNotifications(value) {
        const normalized = value === 0 || value === '0' ? '0' : (value === 1 || value === '1' ? '1' : null);
        if (normalized === null) return;
        if (normalized === (inNotifications === 0 || inNotifications === '0' ? '0' : '1')) return;
        if (!settingInNotif) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
            return;
        }
        try {
            await postRequest(`/api/module_settings/update/${settingInNotif.id}`, { value: normalized }, true);
            dispatch({ type: 'update_module_setting', id: settingInNotif.id, value: normalized });
            toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ValueChanged') });
        } catch (e) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
        }
    }

    async function changeInLists(value) {
        const normalized = value === 0 || value === '0' ? '0' : (value === 1 || value === '1' ? '1' : null);
        if (normalized === null) return;
        if (normalized === (inLists === 0 || inLists === '0' ? '0' : '1')) return;
        if (!settingInLists) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
            return;
        }
        try {
            await postRequest(`/api/module_settings/update/${settingInLists.id}`, { value: normalized }, true);
            dispatch({ type: 'update_module_setting', id: settingInLists.id, value: normalized });
            toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ValueChanged') });
        } catch (e) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
        }
    }

    async function changeExcludedMachines(value) {
        const normalized = value === 0 || value === '0' ? '0' : (value === 1 || value === '1' ? '1' : null);
        if (normalized === null) return;
        if (normalized === (excludedMachines === 0 || excludedMachines === '0' ? '0' : '1')) return;
        if (!settingExcludedMachines) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
            return;
        }
        try {
            await postRequest(`/api/module_settings/update/${settingExcludedMachines.id}`, { value: normalized }, true);
            dispatch({ type: 'update_module_setting', id: settingExcludedMachines.id, value: normalized });
            toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ValueChanged') });
        } catch (e) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
        }
    }

    async function changeExcludedCompanies(value) {
        const normalized = value === 0 || value === '0' ? '0' : (value === 1 || value === '1' ? '1' : null);
        if (normalized === null) return;
        if (normalized === (excludedCompanies === 0 || excludedCompanies === '0' ? '0' : '1')) return;
        if (!settingExcludedCompanies) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
            return;
        }
        try {
            await postRequest(`/api/module_settings/update/${settingExcludedCompanies.id}`, { value: normalized }, true);
            dispatch({ type: 'update_module_setting', id: settingExcludedCompanies.id, value: normalized });
            toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ValueChanged') });
        } catch (e) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
        }
    }

    async function updateSemaforSetting(setting, value, currentValue) {
        if (!setting) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
            return;
        }
        if (!COLOR_VALUES.includes(value)) return;
        if (value === currentValue) return;
        try {
            await postRequest(`/api/module_settings/update/${setting.id}`, { value }, true);
            dispatch({ type: 'update_module_setting', id: setting.id, value });
            toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.SemaforColorChanged') });
        } catch (e) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
        }
    }

    async function updateGraphColorSetting(setting, value, currentValue) {
        if (!setting) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
            return;
        }
        if (!COLOR_VALUES.includes(value)) return;
        if (value === currentValue) return;
        try {
            await postRequest(`/api/module_settings/update/${setting.id}`, { value }, true);
            dispatch({ type: 'update_module_setting', id: setting.id, value });
            toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ColorChangedSuccess') });
        } catch (e) {
            toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
        }
    }

  return (
    <div className="relative flex flex-col h-[calc(100vh-137px)] bg-gray-100 overflow-y-auto overflow-x-hidden">
      <div className="p-4 w-full">
        <h1 className="text-3xl font-semibold text-left text-zinc-900 mb-4">{t('CustomerGeneralSettings.Title')}</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full items-stretch">
          {/* 1. okno: Zobrazenie deaktivovaných PU, vyradených strojov, vyradených serv. firiem */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('Ui.R67.Zobrazenie_deaktivovanych_planovanych_ukonov')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Deaktivovane_planovane_ukony_mozete_zobrazit_alebo_skryt_v_n')}</p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200 mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-[minmax(120px,1fr)_auto] gap-3 items-center">
                <label className="text-sm font-medium text-gray-800">{t('Ui.R67.V_notifikaciach')}</label>
                <div className="flex items-center gap-2 shrink-0">
                  <SelectButton
                    value={inNotifications}
                    onChange={(e) => changeInNotifications(e.value)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[minmax(120px,1fr)_auto] gap-3 items-center">
                <label className="text-sm font-medium text-gray-800">V zoznamoch:</label>
                <div className="flex items-center gap-2 shrink-0">
                  <SelectButton
                    value={inLists}
                    onChange={(e) => changeInLists(e.value)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
            </div>

            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-2">{t('Ui.R67.Zobrazenie_vyradenych_strojov')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Vyradene_stroje_mozete_zobrazit_alebo_skryt_v_zoznamoch_udrz')}</p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200 mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-[minmax(120px,1fr)_auto] gap-3 items-center">
                <label className="text-sm font-medium text-gray-800">{t('Ui.R67.Zobrazenie_vyradenych_strojov_2')}</label>
                <div className="flex items-center gap-2 shrink-0">
                  <SelectButton
                    value={excludedMachines}
                    onChange={(e) => changeExcludedMachines(e.value)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
            </div>

            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-2">{t('Ui.R67.Zobrazenie_vyradenych_servisnych_firiem')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Vyradene_servisne_firmy_mozete_zobrazit_alebo_skryt_v_zoznam')}</p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-[minmax(120px,1fr)_auto] gap-3 items-center">
                <label className="text-sm font-medium text-gray-800">{t('Ui.R67.Zobrazenie_vyradenych_servisnych_firiem_2')}</label>
                <div className="flex items-center gap-2 shrink-0">
                  <SelectButton
                    value={excludedCompanies}
                    onChange={(e) => changeExcludedCompanies(e.value)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 2. okno: Farby pre zoznamy */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              Farby pre zoznamy
            </h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Tieto_farby_sa_pouzivaju_v_zozname_platnosti_udrzby_semafor_')}</p>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
              {getSemaforKeys().map(({ key, label }) => {
                const setting = module_settings.find(s => s.module === 'maintenance' && s.key === key);
                const defaultValue = key === 'mntn_semafor_0' ? 'red' : (key === 'mntn_semafor_dea' ? 'gray' : (key === 'mntn_semafor_60_plus' ? 'green' : 'orange'));
                const currentValue = getSemaforSetting(module_settings, key, defaultValue);
                const colorClass = getSemaforColorClass(currentValue);
                return (
                  <div key={key} className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_220px] gap-3 items-center py-1.5">
                    <label className="text-sm font-medium text-gray-800 truncate">{label}</label>
                    <div className="flex items-center gap-2 shrink-0 min-w-0">
                      <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
                      <Dropdown
                        value={currentValue}
                        options={SEMAFOR_COLOR_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => updateSemaforSetting(setting, e.value, currentValue)}
                        className="w-[180px] border border-gray-400 rounded"
                        placeholder={uiT('Common.Color')}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 3. okno: Farby pre grafy v náhľade údržby */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('Ui.R67.Farby_pre_grafy_v_nahlade_udrzby')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Nastavenie_farieb_grafov_na_stranke_Nahlad_udrzby_segmenty_P')}</p>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
              {getGraphMaintenanceKeys().map(({ key, label }) => {
                const setting = module_settings.find(s => s.module === 'maintenance' && s.key === key);
                const defaultValue = key === 'mntn_graph_0' ? 'red' : (key === 'mntn_graph_ok' ? 'green' : 'orange');
                const currentValue = getColorSetting(module_settings, key, defaultValue);
                const colorClass = getSemaforColorClass(currentValue);
                return (
                  <div key={key} className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_220px] gap-3 items-center py-1.5">
                    <label className="text-sm font-medium text-gray-800 truncate">{label}</label>
                    <div className="flex items-center gap-2 shrink-0 min-w-0">
                      <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
                      <Dropdown
                        value={currentValue}
                        options={SEMAFOR_COLOR_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => updateGraphColorSetting(setting, e.value, currentValue)}
                        className="w-[180px] border border-gray-400 rounded"
                        placeholder={uiT('Common.Color')}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
